"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { cn } from "@/lib/utils"
import { ThinkingFieldRingsSVG } from "@/components/thinking/thinking-field-rings-svg"
import type { ThinkingConfig, ThinkingContext } from "@/lib/logosos/ui/thinking"
import { resolveThinkingRuntime } from "@/lib/logosos/ui/thinking"

interface ThinkingVisualProps {
  config: ThinkingConfig
  context: ThinkingContext
  className?: string
  onPulse?: () => void
}

export function ThinkingVisual({ config, context, className, onPulse }: ThinkingVisualProps) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [shouldPulse, setShouldPulse] = useState(false)
  const [shouldSpin, setShouldSpin] = useState(false)
  const breathCycleCount = useRef(0)

  const isBrandElement = context === "watermark-large" || context === "inline" || context === "watermark"
  const isEnabled =
    isBrandElement || (typeof window !== "undefined" && process.env.NEXT_PUBLIC_FF_THINKING_VISUAL_ENABLED === "true")

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    setPrefersReducedMotion(mediaQuery.matches)

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener("change", handler)
    return () => mediaQuery.removeEventListener("change", handler)
  }, [])

  const runtime = resolveThinkingRuntime(config, {
    prefersReducedMotion,
    context,
  })

  useEffect(() => {
    if (config.active && onPulse) {
      setShouldPulse(true)
      const timer = setTimeout(() => setShouldPulse(false), 500)
      return () => clearTimeout(timer)
    }
  }, [config.active, onPulse])

  useEffect(() => {
    if (!runtime.featuresEnabled.spin || !runtime.shouldAnimate || !runtime.spinState) {
      return
    }

    const { breathsRemaining, durationMs: spinDurationMs } = runtime.spinState

    const interval = setInterval(() => {
      breathCycleCount.current += 1
      if (breathCycleCount.current >= breathsRemaining) {
        setShouldSpin(true)
        setTimeout(() => setShouldSpin(false), spinDurationMs)
        breathCycleCount.current = 0
      }
    }, runtime.durationMs)

    return () => clearInterval(interval)
  }, [runtime.featuresEnabled.spin, runtime.shouldAnimate, runtime.durationMs, runtime.spinState])

  if (!isEnabled) {
    return null
  }

  const sizeClass = context === "inline" ? "w-10" : context === "watermark-large" ? "" : "h-64 w-64 md:h-96 md:w-96"

  const animationDurationCss = `${runtime.durationMs}ms`

  const wrapperTransform = runtime.presentation.inlineScaleBoost
    ? `scale(${runtime.presentation.inlineScaleBoost})`
    : undefined

  const fieldStyle = runtime.shouldAnimate
    ? ({
        animation: `logosiq-breathe-field ${animationDurationCss} cubic-bezier(0.4, 0, 0.2, 1) infinite`,
        "--scale-min": runtime.scaleRange.min,
        "--scale-max": runtime.scaleRange.max,
        "--ring-opacity": runtime.presentation.ringOpacity,
        "--breath-opacity-min": context === "inline" ? 1.0 : runtime.presentation.breathOpacityMin,
        "--breath-opacity-max": context === "inline" ? 1.0 : runtime.presentation.breathOpacityMax,
        color: "rgb(59, 130, 246)", // Tailwind blue-500 - matches send button CTA
        transformOrigin: "center center",
        willChange: "transform, opacity, filter",
      } as React.CSSProperties)
    : {
        transformOrigin: "center center",
        opacity: runtime.presentation.ringOpacity,
        color: "rgb(59, 130, 246)", // Tailwind blue-500 - matches send button CTA
      }

  if (runtime.featuresEnabled.glow && runtime.shouldAnimate) {
    if (context === "inline") {
      fieldStyle.animation += `, logosiq-glow-breathe-inline ${animationDurationCss} cubic-bezier(0.4, 0, 0.2, 1) infinite`
      ;(fieldStyle as any)["--glow-inline-opacity-base"] = runtime.presentation.glowStrength
    } else {
      const glowRadiusPx = context === "overlay" ? 23 : 10
      const glowOpacityBase = context === "overlay" ? 0.33 : 0.15

      fieldStyle.animation += `, logosiq-glow-breathe ${animationDurationCss} cubic-bezier(0.4, 0, 0.2, 1) infinite`
      ;(fieldStyle as any)["--glow-radius"] = `${glowRadiusPx}px`
      ;(fieldStyle as any)["--glow-opacity-base"] = glowOpacityBase
    }
  }

  if (runtime.featuresEnabled.drift && runtime.shouldAnimate) {
    fieldStyle.animation += `, logosiq-drift ${runtime.presentation.driftPeriodMs}ms ease-in-out infinite`
    ;(fieldStyle as any)["--drift-amp-x"] = `${runtime.presentation.driftAmplitude.x}px`
    ;(fieldStyle as any)["--drift-amp-y"] = `${runtime.presentation.driftAmplitude.y}px`
  }

  let shimmerClass = ""
  if (runtime.featuresEnabled.shimmerDrift && runtime.featuresEnabled.shimmerBreath) {
    shimmerClass = "logosiq-shimmer-both"
  } else if (runtime.featuresEnabled.shimmerDrift) {
    shimmerClass = "logosiq-shimmer-drift"
  } else if (runtime.featuresEnabled.shimmerBreath) {
    shimmerClass = "logosiq-shimmer-breath"
  }

  const shimmerStyle =
    shimmerClass && runtime.shouldAnimate
      ? ({
          "--shimmer-drift-period": `${runtime.presentation.shimmerDriftPeriodMs}ms`,
          "--shimmer-breath-period": `${runtime.presentation.shimmerBreathPeriodMs}ms`,
          "--shimmer-peak-alpha": runtime.presentation.shimmerPeakAlpha,
          "--shimmer-band-width": `${runtime.presentation.shimmerBandWidthPct}%`,
          "--shimmer-blur": `${runtime.presentation.shimmerBlurPx}px`,
        } as React.CSSProperties)
      : undefined

  const pulseStyle = shouldPulse ? { animation: "logosiq-pulse-once 500ms ease-out forwards" } : undefined

  const spinStyle =
    shouldSpin && runtime.spinState
      ? ({
          animation: `logosiq-spin-tilt ${runtime.spinState.durationMs}ms cubic-bezier(0.45, 0, 0.55, 1) forwards`,
          "--spin-angle": `${runtime.spinState.rotationAngleDeg}deg`,
        } as React.CSSProperties)
      : undefined

  return (
    <div
      className={cn(
        "pointer-events-none select-none",
        context === "watermark" && "absolute inset-0 flex items-center justify-center",
        context === "watermark-large" && "absolute inset-0 flex items-center justify-center",
        className,
      )}
      aria-hidden="true"
      aria-live="polite"
      aria-label={config.active ? "AI is thinking" : "AI is idle"}
      data-thinking-active={runtime.shouldAnimate}
      data-breath-mode={config.breathMode}
      data-context={context}
      data-ring-opacity={runtime.presentation.ringOpacity}
      data-breath-opacity-range={`${runtime.presentation.breathOpacityMin}-${runtime.presentation.breathOpacityMax}`}
      data-ring-stroke="rgb(59, 130, 246)"
    >
      <div
        className={cn(sizeClass, "relative flex items-center justify-center", context === "inline" && "min-h-10")}
        style={{
          transformOrigin: "center center",
          color: "rgb(59, 130, 246)",
          transform: wrapperTransform,
          overflow: "visible",
        }}
      >
        <div
          className={cn("absolute inset-0 flex items-center justify-center", shimmerClass)}
          style={
            {
              ...fieldStyle,
              ...pulseStyle,
              ...spinStyle,
              ...shimmerStyle,
            } as React.CSSProperties
          }
          data-animation-target="field-wrapper"
          data-layer="field"
          data-ring-count={runtime.ringCount}
        >
          <ThinkingFieldRingsSVG
            className="h-full w-full"
            ringCount={runtime.ringCount}
            outerRingBoost={runtime.presentation.outerRingBoost}
          />
        </div>
      </div>
    </div>
  )
}
